import React, { useState, useEffect } from "react";
import { AgCharts } from "ag-charts-react";
import { ModuleRegistry, AllEnterpriseModule } from 'ag-grid-enterprise';
import "ag-charts-enterprise";
import './OutlookChart.scss';
import countriesDictionary from '../../config/countriesDictionary.js';
import { UNITS_DICTIONARY, ACTIVITY_UNITS } from '../../config/unitsDictionary.js';

// Register all Community features
ModuleRegistry.registerModules([AllEnterpriseModule]);

// data={data} countries={parsedRegions} selectedValues={selectedValues} products={parsedProducts} title={title}
const OutlookChart = ({ data, countries, selectedValues, products, title, onDataRendered }) => {
    const [chartData, setChartData] = useState([]);
    const [chartOptions, setChartOptions] = useState({});

    useEffect(() => {
        // Transform data for charting based on filters
        const chartData = transformDataForChart(data, selectedValues);
        setChartData(chartData);
        const options = buildChartOptions(chartData, selectedValues);
        setChartOptions(options);

        console.log('Chart Data:', chartData);
        console.log('Chart Options:', options);

        if (onDataRendered) onDataRendered();
    }, [data]);

    const transformDataForChart = (data) => {
        if (!Array.isArray(data) || data.length === 0) return [];

        const { products = [], activities = [], sectors = [], countries = [] } = selectedValues;
        // Build series info for both country and subregion, including sector
        const seriesInfo = [];

        // Build the series info based on selected filters
        products.forEach(product => {
            const [productName, sectorName] = product.split('-'); // Handle product|sector format
            activities.forEach(activity => {
                countries.forEach(country => {
                    const [ region, countryName, subregionName ] = country.split('.'); // Handle country.subregion format

                    const unitLabel = ACTIVITY_UNITS[activity] ? ` (${UNITS_DICTIONARY[ACTIVITY_UNITS[activity]].shortLabel})` : '';
                    const countryLabel = countriesDictionary[country]?.label || country;
                    const sectorLabel = sectorName ? ` - ${sectorName}` : '';
                    // Country-level series
                    
                    seriesInfo.push({
                        name: `${product}${sectorLabel} - ${activity}${unitLabel} - ${countryLabel} (ALL SUBREGIONS)`,
                        key: `${productName}${sectorName ? "-" + sectorName : ""}-${activity}-${countryName}${subregionName ? "-" + subregionName : ""}`
                    });
                });
            });
        });

        console.log('SeriesInfo:', seriesInfo);

        const monthAbbr = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

        const yearMonths = data.map(item => {
            const m = Number(item.month);
            const abbr = (m >= 1 && m <= 12) ? monthAbbr[m - 1] : item.month;
            return `${item.year} ${abbr}`;
        });

        console.log('Data:', data);
        console.log('YearMonths:', yearMonths);
        console.log('SeriesInfo:', seriesInfo);

        let chartData = {};

        // Map data to the chart structure
        data.map((item, idx) => {
            const yearMonth = yearMonths[idx];
            const val = item?.value;
            
            chartData[yearMonth] = chartData[yearMonth] || { month: yearMonth };

            // if (val === undefined || val === null) {
            //     return;
            // }

            let { product, sector, activity, country, subregion } = item;
            if (
                subregion === "NA" || 
                subregion ==="ALL SUBREGIONS" || 
                subregion === "" ||
                 subregion === "ALL") subregion = null; // Handle "NA" as no subregion
            const itemKey = `${product}${sector ? "-" + sector : ""}-${activity}-${country}${subregion ? "-" + subregion : ""}`
            console.log('Processing item:', itemKey);
            seriesInfo.forEach(({ name, key }) => {
                if (key === itemKey) {
                    if (val === undefined || val === null) {
                        chartData[yearMonth][key] = val;
                    } else {
                        const num = Number(val);
                        chartData[yearMonth][key] = isNaN(num) ? undefined : Math.round(num * 10) / 10;
                    }
                } else {                    
                    return
                }
            });
        });
        console.log('Transformed Chart Data:', chartData);
        // Convert from object to array
        chartData = Object.values(chartData);
        console.log('Final Chart Data Array:', chartData);
        return chartData;
    };

    const buildChartOptions = (data) => {
        // Ensure products, activities, countries are defined
        const products = selectedValues.products || [];
        const activities = selectedValues.activities || [];
        const countries = selectedValues.countries || [];
        // Build series labels for both country and subregion
        const seriesLabels = [];
        products.forEach(product => {
            activities.forEach(activity => {
                countries.forEach(country => {
                    const unitLabel = ACTIVITY_UNITS[activity] ? ` (${UNITS_DICTIONARY[ACTIVITY_UNITS[activity]].shortLabel})` : '';
                    const countryLabel = countriesDictionary[country]?.label || country;
                    const [ regionName, countryName, subregionName ] = country.split('.'); // Handle country.subregion format
                    // Country-level
                    seriesLabels.push({
                        seriesName: `${product}-${activity}-${countryName}${subregionName ? "-" + subregionName : ""}`,
                        label: `${product} - ${activity}${unitLabel} - ${countryLabel} (ALL REGIONS)`
                    });
                    // Subregion-level
                    if (selectedValues.subregions) {
                        selectedValues.subregions
                            .filter(sr => sr.country === country)
                            .forEach(sr => {
                                seriesLabels.push({
                                    seriesName: `${product}-${activity}-${country}-${sr.subregion}`,
                                    label: `${product} - ${activity}${unitLabel} - ${countryLabel} (${sr.subregion})`
                                });
                            });
                    }
                });
            });
        });

        console.log('SeriesLabels:', seriesLabels);
        return {
            autoSize: true,
            height: 600,
            data: data,
            animation: {
                enabled: true,
                duration: 800,
            },
            series: seriesLabels.map(({ seriesName, label }) => ({
                type: 'line',
                xKey: 'month',
                yKey: seriesName,
                yName: label,
                title: label,
                strokeWidth: 2.5,
                marker: {
                    enabled: true,
                    size: 7,
                    strokeWidth: 2
                },
                // Optionally, you can add a custom tooltip here if needed
            })),
            axes: {
                x: {
                    type: 'category',
                    title: { text: 'Month' },
                    bandHighlight: { enabled: true },
                },
                y: {
                    type: 'number',
                    position: 'right',
                },
            },
            legend: { position: 'bottom' },
            tooltip: {
                enabled: true,
                mode: 'shared',
                position: {
                    anchorTo: "pointer",
                    placement: ["right", "left", "top", "bottom"],
                    xOffset: 20,
                    yOffset: -20,
                },
            },
        };
    };

    return (
        <> 
            <AgCharts key={JSON.stringify(chartOptions)} options={chartOptions} />
        </>
    );
};

export default OutlookChart;